import { useCallback, useEffect, useRef, useState } from "react";
import { ScrollArea } from "../../primitives/scroll-area";
import { Button } from "../../primitives/button";
import { Textarea } from "../../primitives/textarea";
import { Send, Loader2, Sparkles, Check, Copy } from "lucide-react";
import { cn } from "../../primitives/utils";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  DefinitionAssistantChatRequest,
  DefinitionAssistantChatResponse,
} from "@shared-core/data/api-types";

export interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  suggestedExpressions?: Record<string, string> | null;
  suggestedName?: string | null;
  suggestedFamily?: string | null;
  suggestedDescription?: string | null;
}

interface DefinitionAssistantPanelProps {
  definitionType: "signal" | "alert" | "opportunity";
  datasetIds?: string[];
  currentExpressions?: Record<string, string>;
  onAcceptExpressions: (expressions: Record<string, string>) => void;
  onAcceptHeader?: (fields: {
    name?: string;
    family?: string;
    description?: string;
  }) => void;
}

let messageIdCounter = 0;
function nextId() {
  return `msg-${++messageIdCounter}`;
}

async function fetchAssistantResponse(
  request: DefinitionAssistantChatRequest,
): Promise<DefinitionAssistantChatResponse> {
  const baseUrl = getRuntimeConfig().apiBaseUrl;

  if (!baseUrl) {
    // Mock mode: return a simulated response after a short delay
    await new Promise((r) => setTimeout(r, 800));
    return {
      reply:
        "I'd be happy to help you create expressions! Could you describe what business condition you want to detect? For example, you might want to flag customers whose revenue has dropped or who haven't ordered recently.",
      suggested_expressions: null,
      suggested_name: null,
      suggested_family: null,
      suggested_description: null,
    };
  }

  const res = await fetch(
    `${baseUrl}/recommendations/actions/generate-assistant-response`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    },
  );
  if (!res.ok) {
    throw new Error(`Assistant request failed: ${res.status}`);
  }
  return res.json();
}

function MessageBubble({ message }: { message: AssistantMessage }) {
  const isUser = message.role === "user";

  function renderContent(text: string) {
    // Strip the json block from displayed text since we render it as a card
    const cleaned = text.replace(/```json\s*\n?[\s\S]*?\n?\s*```/g, "").trim();
    if (!cleaned) return null;

    const parts = cleaned.split(/(```[\s\S]*?```)/);
    return parts.map((part, i) => {
      if (part.startsWith("```")) {
        const lines = part.slice(3, -3).split("\n");
        const lang = lines[0]?.trim();
        const code = lang ? lines.slice(1).join("\n") : lines.join("\n");
        return (
          <pre
            key={i}
            className="bg-muted rounded-md p-3 my-2 text-xs overflow-x-auto whitespace-pre-wrap"
          >
            <code>{code}</code>
          </pre>
        );
      }
      return (
        <span key={i} className="whitespace-pre-wrap">
          {part}
        </span>
      );
    });
  }

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={cn(
          "max-w-[90%] rounded-lg px-3 py-2 text-sm",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {renderContent(message.content)}
      </div>
    </div>
  );
}

function SuggestionCard({
  expressions,
  suggestedName,
  suggestedFamily,
  suggestedDescription,
  onAcceptAllExpressions,
  onAcceptHeader,
  accepted,
}: {
  expressions: Record<string, string> | null;
  suggestedName?: string | null;
  suggestedFamily?: string | null;
  suggestedDescription?: string | null;
  onAcceptAllExpressions?: () => void;
  onAcceptHeader?: () => void;
  accepted: boolean;
}) {
  const capabilities = usePlatformCapabilities();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  function handleCopyField(key: string, value: string) {
    void capabilities.copyToClipboard(value);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  }

  const hasHeader = Boolean(
    suggestedName || suggestedFamily || suggestedDescription,
  );
  const hasExpressions = Boolean(
    expressions && Object.keys(expressions).length > 0,
  );

  return (
    <div className="mb-3 mx-1">
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        {hasHeader && (
          <div className="px-3 py-2 border-b bg-muted/50 rounded-t-lg">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs text-muted-foreground space-y-0.5 flex-1 min-w-0">
                {suggestedName && (
                  <div>
                    Name:{" "}
                    <span className="font-medium text-foreground">
                      {suggestedName}
                    </span>
                  </div>
                )}
                {suggestedFamily && (
                  <div>
                    Family:{" "}
                    <span className="font-medium text-foreground">
                      {suggestedFamily}
                    </span>
                  </div>
                )}
                {suggestedDescription && (
                  <div>
                    Description:{" "}
                    <span className="font-medium text-foreground">
                      {suggestedDescription}
                    </span>
                  </div>
                )}
              </div>
              {onAcceptHeader && !accepted && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs flex-shrink-0"
                  onClick={onAcceptHeader}
                >
                  <Check className="size-3 mr-1" />
                  Accept
                </Button>
              )}
            </div>
          </div>
        )}
        {hasExpressions && expressions && (
          <>
            <div className="p-3 space-y-2">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Suggested expressions
              </div>
              {Object.entries(expressions).map(([key, value]) => (
                <div key={key} className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      {key}
                    </span>
                    <button
                      onClick={() => handleCopyField(key, value)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title="Copy expression"
                    >
                      {copiedField === key ? (
                        <Check className="size-3 text-green-500" />
                      ) : (
                        <Copy className="size-3" />
                      )}
                    </button>
                  </div>
                  <pre className="bg-muted rounded px-2 py-1.5 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                    {value}
                  </pre>
                </div>
              ))}
            </div>
            <div className="px-3 pb-3">
              {accepted ? (
                <div className="flex items-center gap-1.5 text-xs text-green-600">
                  <Check className="size-3" />
                  Accepted into form
                </div>
              ) : (
                <Button
                  size="sm"
                  className="w-full"
                  onClick={onAcceptAllExpressions}
                >
                  <Check className="size-3 mr-1.5" />
                  Accept all into form
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function DefinitionAssistantPanel({
  definitionType,
  datasetIds,
  currentExpressions,
  onAcceptExpressions,
  onAcceptHeader,
}: DefinitionAssistantPanelProps) {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedMessageIds, setAcceptedMessageIds] = useState<Set<string>>(
    new Set(),
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const sendPrompt = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;

      setError(null);

      const userMsg: AssistantMessage = {
        id: nextId(),
        role: "user",
        content: text,
      };
      const updatedMessages = [...messages, userMsg];
      setMessages(updatedMessages);
      setLoading(true);

      try {
        const request: DefinitionAssistantChatRequest = {
          definition_type: definitionType,
          messages: updatedMessages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          context:
            datasetIds || currentExpressions
              ? {
                  dataset_ids: datasetIds ?? null,
                  current_expressions: currentExpressions ?? null,
                }
              : null,
        };

        const response = await fetchAssistantResponse(request);

        const assistantMsg: AssistantMessage = {
          id: nextId(),
          role: "assistant",
          content: response.reply,
          suggestedExpressions: response.suggested_expressions,
          suggestedName: response.suggested_name,
          suggestedFamily: response.suggested_family,
          suggestedDescription: response.suggested_description,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [loading, messages, definitionType, datasetIds, currentExpressions],
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    await sendPrompt(text);
  }, [input, loading, sendPrompt]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleAcceptExpressions(
    msgId: string,
    expressions: Record<string, string>,
  ) {
    onAcceptExpressions(expressions);
    setAcceptedMessageIds((prev) => new Set(prev).add(msgId));
  }

  function handleAcceptHeader(msg: AssistantMessage) {
    onAcceptHeader?.({
      name: msg.suggestedName ?? undefined,
      family: msg.suggestedFamily ?? undefined,
      description: msg.suggestedDescription ?? undefined,
    });
    setAcceptedMessageIds((prev) => new Set(prev).add(msg.id));
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b flex items-center gap-2">
        <Sparkles className="size-4 text-primary" />
        <span className="text-sm font-medium">Expression Assistant</span>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-3 py-2" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center text-muted-foreground gap-2 py-12 px-4 text-center">
            <Sparkles className="size-8 opacity-40" />
            <p className="text-sm">
              Describe what you want to detect and I'll write the expressions
              for you
            </p>
            <p className="text-xs opacity-70">
              Try: "Flag customers whose 30-day revenue dropped more than 50%"
            </p>
          </div>
        )}
        {messages.map((msg) => {
          const hasExpressions = Boolean(
            msg.suggestedExpressions &&
            Object.keys(msg.suggestedExpressions).length > 0,
          );
          const hasHeader = Boolean(
            msg.suggestedName ||
            msg.suggestedFamily ||
            msg.suggestedDescription,
          );
          return (
            <div key={msg.id}>
              <MessageBubble message={msg} />
              {(hasExpressions || hasHeader) && (
                <SuggestionCard
                  expressions={msg.suggestedExpressions ?? null}
                  suggestedName={msg.suggestedName}
                  suggestedFamily={msg.suggestedFamily}
                  suggestedDescription={msg.suggestedDescription}
                  accepted={acceptedMessageIds.has(msg.id)}
                  onAcceptAllExpressions={
                    hasExpressions
                      ? () =>
                          handleAcceptExpressions(
                            msg.id,
                            msg.suggestedExpressions!,
                          )
                      : undefined
                  }
                  onAcceptHeader={
                    hasHeader && onAcceptHeader
                      ? () => handleAcceptHeader(msg)
                      : undefined
                  }
                />
              )}
            </div>
          );
        })}
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="bg-muted rounded-lg px-3 py-2 text-sm flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-3 animate-spin" />
              Thinking...
            </div>
          </div>
        )}
      </ScrollArea>

      {/* Error */}
      {error && (
        <div className="px-3 py-1 text-xs text-destructive border-t border-border">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-3">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want to detect..."
            className="min-h-[44px] max-h-[120px] resize-none"
            rows={1}
            disabled={loading}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 self-end"
          >
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
